import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { ResolvedTooltip } from '../../model/screencast-tooltips/types';

import styles from './ScreencastTooltip.module.scss';

const TOOLTIP_OFFSET = 12;
const VIEWPORT_MARGIN = 8;

type TooltipScreenPosition = {
  x: number;
  y: number;
};

type ScreencastTooltipProps = {
  tooltip: ResolvedTooltip | null;
  cursorPosition: TooltipScreenPosition | null;
};

// eslint-disable-next-line mobx/missing-observer
export const ScreencastTooltip = ({ tooltip, cursorPosition }: ScreencastTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!tooltip || !cursorPosition || tooltip.lines.length === 0 || typeof window === 'undefined') {
      return;
    }

    const tooltipElement = tooltipRef.current;
    if (!tooltipElement) {
      return;
    }

    const tooltipRect = tooltipElement.getBoundingClientRect();
    const viewportMaxX = window.innerWidth - VIEWPORT_MARGIN;
    const viewportMaxY = window.innerHeight - VIEWPORT_MARGIN;

    let x = cursorPosition.x + TOOLTIP_OFFSET;
    let y = cursorPosition.y + TOOLTIP_OFFSET;

    if (x + tooltipRect.width > viewportMaxX) {
      x = cursorPosition.x - tooltipRect.width - TOOLTIP_OFFSET;
    }

    if (y + tooltipRect.height > viewportMaxY) {
      y = cursorPosition.y - tooltipRect.height - TOOLTIP_OFFSET;
    }

    tooltipElement.style.transform = `translate(${Math.max(VIEWPORT_MARGIN, x)}px, ${Math.max(VIEWPORT_MARGIN, y)}px)`;
  }, [cursorPosition, tooltip]);

  if (!tooltip || !cursorPosition || tooltip.lines.length === 0) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={styles.ScreencastTooltip}
      style={{
        left: 0,
        top: 0,
      }}
    >
      {tooltip.lines.map((line, index) => {
        if (line.kind === 'value') {
          const label = line.distribution.title?.trim();
          const unit = line.distribution.unit?.trim();
          const valueText = unit ? `${line.formattedValue} ${unit}` : line.formattedValue;

          return (
            <div key={`${line.kind}-${index}`} className={styles.ScreencastTooltipValueRow}>
              <span className={styles.ScreencastTooltipLabel}>{label ?? ''}</span>
              <span className={styles.ScreencastTooltipValue}>{valueText}</span>
            </div>
          );
        }

        return (
          <div key={`${line.kind}-${index}`} className={styles.ScreencastTooltipTextRow}>
            {line.text}
          </div>
        );
      })}
    </div>,
    document.body,
  );
};
